import { useEffect, useMemo, useState } from 'react';
import { fileApi } from '@/api/file';
import { projectApi } from '@/api/project';
import { resolveEditableDraft } from '@/lib/draftUtils';
import {
  buildDraftPreviewHtml,
  stripLegacyLandingPageTrackingScript,
  stripLinkerScript,
} from '@/lib/domUtils';
import { isSpecialProjectType } from '@/lib/projectUtils';
import {
  areStudioAnalyticsSettingsEqual,
  normalizeStudioAnalyticsSettings,
  parseStudioAnalyticsSettingsFromHtml,
  type StudioAnalyticsSettings,
  upsertStudioAnalyticsInHtml,
} from '@/lib/studioAnalytics';
import { trackStudioEvent } from '@/lib/studioTracking';
import { getTrackingErrorCode } from '@/lib/trackingUtils';
import { cn } from '@/lib/utils';
import { useStudioStore } from '@/store/useStudioStore';

type AnalyticsItemId =
  | 'analyticsViews'
  | 'analyticsScroll'
  | 'analyticsHeatmap'
  | 'analyticsForm'
  | 'analyticsExit'
  | 'analyticsUtm';

type AnalyticsStatusState = {
  message: string;
  type: 'ok' | 'warn';
};

const LEGACY_ANALYTICS_ENABLED: Record<AnalyticsItemId, boolean> = {
  analyticsViews: true,
  analyticsScroll: true,
  analyticsHeatmap: true,
  analyticsForm: true,
  analyticsExit: false,
  analyticsUtm: true,
};

const ANALYTICS_ITEMS = [
  {
    id: 'analyticsViews' as const,
    label: 'Page Views & Sessions',
    desc: 'Basic traffic and visit tracking',
  },
  {
    id: 'analyticsScroll' as const,
    label: 'Scroll Depth',
    desc: 'Track how far visitors read (25% / 50% / 75% / 100%)',
  },
  {
    id: 'analyticsHeatmap' as const,
    label: 'Click Heatmap',
    desc: 'Record all click positions for visual analysis',
  },
  {
    id: 'analyticsForm' as const,
    label: 'Form Interactions',
    desc: 'Track field focus, abandonment, and submission events',
    na: true,
  },
  {
    id: 'analyticsExit' as const,
    label: 'Exit Intent',
    desc: 'Detect when visitors are about to leave',
  },
  {
    id: 'analyticsUtm' as const,
    label: 'UTM Attribution',
    desc: 'Capture UTM parameters from ads and organic links',
  },
];

type LegacyAnalyticsSettingsPayload = {
  customJs: string;
  enabled: Record<AnalyticsItemId, boolean>;
};

const normalizeLegacyAnalyticsSettings = (
  settings?: Partial<LegacyAnalyticsSettingsPayload & StudioAnalyticsSettings> | null,
): LegacyAnalyticsSettingsPayload => ({
  customJs: typeof settings?.customJs === 'string' ? settings.customJs : '',
  enabled: {
    ...LEGACY_ANALYTICS_ENABLED,
    ...(settings?.enabled || {}),
  },
});

const CUSTOM_JS_ADD_EVENT = 'studio_custom_js_add';
const CUSTOM_JS_SAVE_SUCCESS_EVENT = 'studio_custom_js_save_success';
const CUSTOM_JS_SAVE_FAILURE_EVENT = 'studio_custom_js_save_failure';
const CUSTOM_JS_REPLACE_EVENT = 'studio_custom_js_replace';

const loadLegacyAnalyticsSettings = (storageKey: string) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<
      LegacyAnalyticsSettingsPayload & StudioAnalyticsSettings
    >;
    return normalizeLegacyAnalyticsSettings(parsed);
  } catch {
    return null;
  }
};

const persistLegacyAnalyticsSettings = (
  storageKey: string,
  payload: LegacyAnalyticsSettingsPayload,
) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // HTML persistence is the source of truth.
  }
};

const buildCustomJsSettings = (customJs: string): StudioAnalyticsSettings => ({
  customJs: customJs.trim() ? customJs : '',
});

export const StudioSettingsAnalytics = () => {
  const {
    currentId,
    currentProjectId,
    currentProjectTypeId,
    currentSiteId,
    currentDraftVersion,
    generatedHtml,
    setCurrentDraftMeta,
    setGeneratedHtml,
    setIframeUrl,
  } = useStudioStore();
  const isSpecialProject = isSpecialProjectType(currentProjectTypeId);
  const storageKey = useMemo(
    () => `studio_tracking_${currentId || currentProjectId || 'default'}`,
    [currentId, currentProjectId],
  );
  const [settings, setSettings] = useState<StudioAnalyticsSettings>(() =>
    normalizeStudioAnalyticsSettings(),
  );
  const [savedSettings, setSavedSettings] = useState<StudioAnalyticsSettings>(() =>
    normalizeStudioAnalyticsSettings(),
  );
  const [enabled, setEnabled] =
    useState<Record<AnalyticsItemId, boolean>>(LEGACY_ANALYTICS_ENABLED);
  const [status, setStatus] = useState<AnalyticsStatusState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus(null);

    const loadSourceHtml = async () => {
      if (generatedHtml.trim()) {
        return stripLinkerScript(generatedHtml);
      }

      if (isSpecialProject) {
        if (!currentProjectId) return '';

        try {
          const files = await fileApi.listFiles({
            projectId: Number(currentProjectId),
            path: 'dist',
          });
          const indexFile = files.find((file) => file.name === 'index.html' && !file.directory);
          if (!indexFile) return '';

          const targetPath = indexFile.path.startsWith('dist/')
            ? indexFile.path
            : `dist/${indexFile.path}`;
          const fileContent = await fileApi.readFile({
            projectId: Number(currentProjectId),
            path: targetPath,
          });
          return fileContent.content || '';
        } catch {
          return '';
        }
      }

      if (!currentId) return '';

      try {
        const { editableDraft } = await resolveEditableDraft(Number(currentId));
        return editableDraft?.content || '';
      } catch {
        return '';
      }
    };

    const loadSettings = async () => {
      const sourceHtml = await loadSourceHtml();
      if (cancelled) return;

      const nextSettings = normalizeLegacyAnalyticsSettings(
        parseStudioAnalyticsSettingsFromHtml(sourceHtml) || loadLegacyAnalyticsSettings(storageKey),
      );

      setSettings(normalizeStudioAnalyticsSettings(nextSettings));
      setSavedSettings(normalizeStudioAnalyticsSettings(nextSettings));
      setEnabled(nextSettings.enabled);
    };

    void loadSettings();

    return () => {
      cancelled = true;
    };
  }, [currentId, currentProjectId, generatedHtml, isSpecialProject, storageKey]);

  const hasUnsavedChanges = !areStudioAnalyticsSettingsEqual(settings, savedSettings);

  const getEnabledAnalyticsItems = (items: Record<AnalyticsItemId, boolean>) =>
    Object.entries(items)
      .filter(([, value]) => value)
      .map(([key]) => key);

  const toggle = (id: AnalyticsItemId) => {
    setEnabled((prev) => {
      const nextEnabled = !prev[id];
      const next = { ...prev, [id]: nextEnabled };

      trackStudioEvent('studio_analytics_toggle_change', {
        trackingItem: id,
        nextEnabled,
        enabledItems: getEnabledAnalyticsItems(next),
      });

      return next;
    });
  };

  const persistTrackingToSource = async (nextSettings: StudioAnalyticsSettings) => {
    const runtimeContext = {
      currentUserProjectId: currentId,
      currentProjectId,
      currentProjectTypeId,
      currentSiteId,
    };

    if (isSpecialProject) {
      if (!currentProjectId) {
        throw new Error('No project selected.');
      }

      const files = await fileApi.listFiles({
        projectId: Number(currentProjectId),
        path: 'dist',
      });
      const indexFile = files.find((file) => file.name === 'index.html' && !file.directory);
      if (!indexFile) {
        throw new Error('index.html not found in dist.');
      }

      const targetPath = indexFile.path.startsWith('dist/')
        ? indexFile.path
        : `dist/${indexFile.path}`;
      const fileContent = await fileApi.readFile({
        projectId: Number(currentProjectId),
        path: targetPath,
      });
      const updatedContent = upsertStudioAnalyticsInHtml(
        stripLegacyLandingPageTrackingScript(fileContent.content || ''),
        nextSettings,
        runtimeContext,
      );

      await fileApi.writeFile({
        projectId: Number(currentProjectId),
        path: targetPath,
        content: updatedContent,
      });

      setGeneratedHtml(buildDraftPreviewHtml(updatedContent));
      setIframeUrl(null);
      return;
    }

    if (!currentId) {
      throw new Error('No project selected.');
    }

    const { editableDraft, saveBaseVersion } = await resolveEditableDraft(Number(currentId));
    const sourceContent =
      editableDraft?.content || stripLinkerScript(useStudioStore.getState().generatedHtml || '');
    if (!sourceContent.trim()) {
      throw new Error('No page content available to attach Custom JavaScript.');
    }

    const updatedContent = upsertStudioAnalyticsInHtml(
      stripLegacyLandingPageTrackingScript(sourceContent),
      nextSettings,
      runtimeContext,
    );
    const savedDraft = await projectApi.saveDraft({
      userProjectId: Number(currentId),
      content: updatedContent,
      changeNote: 'Updated custom JavaScript',
      draftStatus: 'CONFIRMED',
      baseVersion: saveBaseVersion ?? editableDraft?.version ?? currentDraftVersion ?? undefined,
    });

    setCurrentDraftMeta(savedDraft.id, savedDraft.version, savedDraft.draftStatus);
    setGeneratedHtml(buildDraftPreviewHtml(savedDraft.content));
  };

  const handleSave = async () => {
    const previousCustomJs = savedSettings.customJs.trim();
    const nextSettings = buildCustomJsSettings(settings.customJs);
    const nextCustomJs = nextSettings.customJs.trim();
    const hadCustomJs = Boolean(previousCustomJs);
    const hasCustomJs = Boolean(nextCustomJs);
    const isReplacingCustomJs = hadCustomJs && hasCustomJs && previousCustomJs !== nextCustomJs;
    const isAddingCustomJs = !hadCustomJs && hasCustomJs;
    const saveTrackingData = {
      customJsLength: nextSettings.customJs.length,
      hasCustomJs,
      previousCustomJsLength: savedSettings.customJs.length,
    };

    setSaving(true);
    try {
      await persistTrackingToSource(nextSettings);
      persistLegacyAnalyticsSettings(storageKey, {
        customJs: nextSettings.customJs,
        enabled,
      });
      setSettings(nextSettings);
      setSavedSettings(nextSettings);
      setStatus({
        type: 'ok',
        message: hasCustomJs
          ? 'Custom JavaScript saved and injected into the current page.'
          : 'Custom JavaScript cleared from the current page.',
      });

      if (isAddingCustomJs) {
        trackStudioEvent(CUSTOM_JS_ADD_EVENT, saveTrackingData);
      }

      if (isReplacingCustomJs) {
        trackStudioEvent(CUSTOM_JS_REPLACE_EVENT, saveTrackingData);
      }

      trackStudioEvent(CUSTOM_JS_SAVE_SUCCESS_EVENT, {
        ...saveTrackingData,
        action: hasCustomJs
          ? isReplacingCustomJs
            ? 'replace'
            : isAddingCustomJs
              ? 'add'
              : 'save'
          : 'clear',
      });
    } catch (error) {
      setStatus({
        type: 'warn',
        message: (error as { message?: string })?.message || 'Failed to save Custom JavaScript.',
      });
      trackStudioEvent(CUSTOM_JS_SAVE_FAILURE_EVENT, {
        ...saveTrackingData,
        errorCode: getTrackingErrorCode(error),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cfg-section active animate-in fade-in duration-200">
      <div className="cfg-sec-title mb-[4px] text-[15px] font-[800] tracking-[-0.3px] text-[#0D0D1A]">
        Analytics
      </div>
      <div className="cfg-sec-sub mb-[18px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
        Choose the visitor activity you want to measure on this page.
      </div>

      <div className="cfg-field">
        <label className="cfg-label mb-[10px] block text-[12px] font-[700] text-[#0D0D1A]/56">
          What to measure
        </label>
        {ANALYTICS_ITEMS.map((item) => (
          <div
            key={item.id}
            className={cn(
              'cfg-toggle-row border-border flex items-center justify-between gap-[12px] border-b py-[11px] last:border-none',
              item.na && 'opacity-[0.38]',
            )}
          >
            <div className="cfg-toggle-info flex-1">
              <div className="cfg-toggle-label text-[13px] font-[600] text-[#0D0D1A]">
                {item.label}
              </div>
              <div className="cfg-toggle-desc mt-[2px] text-[11.5px] text-[#0D0D1A]/38">
                {item.desc}
                {item.na ? (
                  <span className="text-[#0D0D1A]/38 italic">
                    | Not available - no form on this page
                  </span>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              aria-pressed={enabled[item.id]}
              className={cn(
                'cfg-toggle relative h-[21px] w-[38px] shrink-0 rounded-full transition-colors duration-200 after:absolute after:top-[3px] after:left-[3px] after:h-[15px] after:w-[15px] after:rounded-full after:bg-white after:shadow-[0_1px_3px_rgba(0,0,0,0.2)] after:transition-transform after:duration-200',
                enabled[item.id] ? 'bg-[#6D28D9] after:translate-x-[17px]' : 'bg-black/[0.15]',
              )}
              onClick={() => {
                if (!item.na) {
                  toggle(item.id);
                }
              }}
              tabIndex={item.na ? -1 : 0}
              disabled={item.na}
            />
          </div>
        ))}
      </div>

      <div className="cfg-field mt-[18px]">
        <label
          htmlFor="trackingCustomJs"
          className="cfg-label mb-[10px] block text-[12px] font-[700] text-[#0D0D1A]/56"
        >
          Custom JavaScript
        </label>
        <textarea
          id="trackingCustomJs"
          className="cfg-input min-h-[220px] w-full rounded-[8px] border border-[#0D0D1A]/10 bg-white p-[12px_14px] font-mono text-[12px] leading-[1.75] text-[#0D0D1A] transition-colors outline-none focus:border-[#6D28D9]/40"
          spellCheck={false}
          placeholder={`// Paste custom JavaScript here\n// Example: document.addEventListener('click', function () {\n//   console.log('CTA clicked');\n// });`}
          value={settings.customJs}
          onChange={(event) => {
            setSettings({
              customJs: event.target.value,
            });
            setStatus(null);
          }}
        />
        <div className="mt-[6px] text-[11px] text-[#0D0D1A]/40">
          Use this for custom analytics pixels, event listeners, or tracking scripts that are not
          covered by the options above.
        </div>
      </div>

      <div className="mt-[18px]">
        <button
          className="cfg-save-btn inline-flex h-[36px] cursor-pointer items-center justify-center rounded-[9px] bg-[linear-gradient(135deg,#6D28D9,#0891B2)] px-[16px] text-[12.5px] font-[700] text-white transition-opacity hover:opacity-[0.88] disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
        >
          {saving ? 'Saving...' : 'Save Custom JavaScript'}
        </button>
      </div>

      {status ? (
        <div className="mt-[12px] flex items-center gap-[8px] rounded-[8px] border border-[rgba(0,0,0,0.07)] bg-[#F7F7FC] px-[12px] py-[10px] text-[12.5px] font-[600] text-[#0D0D1A]">
          <span
            className={cn(
              'h-[8px] w-[8px] shrink-0 rounded-full',
              status.type === 'ok' ? 'bg-emerald-600' : 'bg-[#D97706]',
            )}
          />
          <span>{status.message}</span>
        </div>
      ) : null}
    </div>
  );
};
